import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, defer, Observable } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { JwtService } from '../api/user-service';

@Injectable()
export class UserServiceJwtInterceptor implements HttpInterceptor {

  private jwt: string = undefined;
  private clockSkew: number = 0; // difference between client and server time (User Service) in milliseconds

  private isRefreshing = false;
  private refreshJwtSubject = new BehaviorSubject<string>(null);

  private URLS_WITHOUT_JWT = [
    '/users/api/jwt',
    '/api/news',
    '/api/version',
  ];

  constructor(
    private jwtService: JwtService,
  ) {
  }

  intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.URLS_WITHOUT_JWT.includes(httpRequest.url)) {
      return next.handle(httpRequest);
    }

    if (!this.isTokenExpired(5)) {
      return next.handle(this.addTokenToRequest(httpRequest));
    }

    return this.updateToken().pipe(
      switchMap(() => {
        return next.handle(this.addTokenToRequest(httpRequest));
      }),
    );
  }

  /**
   * Adds the JWT token to the request header
   */
  private addTokenToRequest(req: HttpRequest<any>): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set('x-user-service-jwt', this.jwt),
    });
  }

  /**
   * Updates the JWT token, when a request is already in progress combines the requests
   * @returns Observable that emits JWT
   */
  private updateToken(): Observable<string> {
    if (this.isRefreshing) {
      return this.refreshJwtSubject.pipe(
        filter(jwt => jwt !== null),
        take(1),
      );
    }

    this.refreshJwtSubject.next(null);
    this.isRefreshing = true;
    return this.fetchJwtWithLatencyMeasurement().pipe(
      tap(jwt => {
        this.isRefreshing = false;
        this.refreshJwtSubject.next(jwt);
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.refreshJwtSubject.next(null);
        throw err;
      }),
    );
  }

  private fetchJwtWithLatencyMeasurement(): Observable<string> {
    return defer(() => {
      const requestStartTime = performance.now();

      return this.jwtService.getJwt().pipe(
        tap(rawJwt => {
          const requestEndTime = performance.now();
          const roundTripTime = requestEndTime - requestStartTime;
          // Estimate one-way latency as half of the round-trip time
          const estimatedNetworkLatency = Math.round(roundTripTime / 2);
          this.setJwt(rawJwt, estimatedNetworkLatency);
        }),
      );
    });
  }

  private setJwt(rawJwt: string, networkLatency: number): void {
    this.jwt = rawJwt;

    const { iat } = jwtDecode(rawJwt);
    // Adjust clock skew calculation to account for network latency
    this.clockSkew = Date.now() - iat * 1000 - networkLatency;
    console.log('[USER SERVICE] Token updated with estimated ClockSkew of ' + (this.clockSkew) + 'ms (including network latency estimation of ' + networkLatency + 'ms)');
  }

  /**
   * Checks if the current token is expired
   * @param minValidity Minimum number of seconds required to consider token valid
   * @returns true if token is expired or will expire within minValidity seconds, false otherwise
   */
  isTokenExpired(minValidity: number = 0): boolean {
    if (!this.jwt) {
      return true;
    }
    return this.getValiditySeconds() <= minValidity;
  }

  getValiditySeconds(): number {
    if (!this.jwt) {
      return 0;
    }
    const { exp } = jwtDecode(this.jwt);
    return Math.max(0, exp - (Date.now() - this.clockSkew) / 1000);
  }
}
